'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/lib/session';
import {
  type CrmJobRun,
  type CrmJobRunMode,
  type CrmTriggerJob,
  type CrmTriggersStatus,
  type OnboardingSignal,
  crmJobRunModeLabels,
  crmJobRunModeValues,
  crmTriggerJobLabels,
  crmTriggerJobValues,
  onboardingSignalLabels,
  onboardingSignalValues,
} from '@borradh-workspace/api-client/types';
import { Play } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import {
  type CrmTriggerRequest,
  useCrmTriggersStatus,
  useRunCrmTrigger,
} from '../../api/crm-triggers';
import { OrgCombobox, type SelectedOrg } from '../org-combobox';
import { HandoffFlowCard } from './handoff-flow-card';

/** What each scheduled job does, and how `now` changes what it finds. */
const JOB_NOTES: Record<CrmTriggerJob, string> = {
  'clinic-health':
    'Scores every clinic 0–100 (runs nightly at 06:00 UTC), writes the band and reasons to Twenty, and DMs a CSM whose clinic dropped a band or 15 points in a week, once a clinic a day. The managers’ channel gets the summary.',
  'onboarding-sync':
    'Reads each onboarding clinic’s platform signals (data received, Stripe, Meta), ticks the checklist in Twenty, moves the stage forward and writes what is missing (runs hourly). Alerts to Eoin, Senan and Daniel go out only in Irish working hours, Monday to Friday: set “Run as of” to a weekday 09:00 Irish time to see them.',
  'onboarding-digest':
    'One DM to Eoin listing every clinic at Onboarding with its stage, days since signing against 21, blocker and what is missing (runs Monday 08:00 Dublin). Sends whenever fired; “Run as of” only changes the day counts.',
  'calendar-sync':
    'For every customer clinic, writes the next booked call with one of its contacts from the team’s Google Calendars to Twenty (empty when nothing is booked) and moves Last touchpoint forward to the latest held call (runs hourly at :30). “Run as of” sets the instant past and future are judged from.',
  'demo-reminders':
    'DMs the rep whose demo starts in 10–20 minutes (runs every 5 minutes). Set “Run as of” to 15 minutes before a booked demo to see one.',
  'contract-nudge':
    'DMs the rep whose contract has sat unsigned for 2 days, up to 30 days back (runs hourly). Each nudge is sent once; a second run says already_sent.',
  'csm-checkins':
    'DMs each CSM the clinics due a check (no touchpoint in 2 days), worst health first, with a Twenty task and a Done button per clinic (runs weekdays 08:30 Dublin). A clinic listed in the last two days is left off.',
};

/** Who a real run messages and what it changes, said before it runs. */
const REAL_RECIPIENTS: Record<CrmTriggerJob, string> = {
  'clinic-health':
    'Each CSM whose clinic got worse, and the managers’ channel. A clinic already alerted about today is not sent again. Writes every clinic’s score to Twenty.',
  'onboarding-sync':
    'Eoin, Senan and Daniel (onboarding alerts, in Irish working hours). Writes the checklist, stage and what is missing to Twenty, and may create tasks.',
  'onboarding-digest': 'Eoin.',
  'calendar-sync':
    'Nobody: it sends no messages. Writes each clinic’s next and last call to Twenty.',
  'demo-reminders': 'The rep whose demo starts in 10–20 minutes.',
  'contract-nudge':
    'The rep whose contract has sat unsigned for 2 days (each nudge once).',
  'csm-checkins':
    'Each CSM with clinics due a check, and the managers’ channel. Creates a Twenty task per clinic.',
};

/** What a production run does for real, said before it runs. */
const PRODUCTION_RUN =
  'This is production: the run reads and writes the live Twenty workspace and DMs real teammates in Slack, exactly as the scheduled job does.';

/** Who a test run may message: someone on the team. The API checks they are in Slack. */
const TEAM_EMAIL = /@borradh\.(io|com)$/;

/** A long list on the card stops here; the JSON below has the rest. */
const LIST_LIMIT = 20;

const DEP_LABELS: Record<keyof CrmTriggersStatus['deps'], string> = {
  twenty: 'Twenty',
  granola: 'Granola',
  slackBot: 'Slack bot',
  managersWebhook: 'Managers channel',
  posthogUsage: 'PostHog usage',
  docuseal: 'DocuSeal',
};

/** A datetime-local value → ISO instant, or undefined when the field is empty. */
const toIso = (local: string): string | undefined => {
  if (!local) return undefined;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const pretty = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * The HTTP status behind a failed request. The client's `ApiFetchError` and
 * `ApiClientError` carry it as `status`; a raw HTTP error carries it on its
 * `response`.
 */
const httpStatus = (error: unknown): number | undefined => {
  const e = error as
    | { status?: unknown; response?: { status?: number } }
    | null
    | undefined;
  return typeof e?.status === 'number' ? e.status : e?.response?.status;
};

/** What the page shows for a failed run: status, code, message and details, as the API sent them. */
const errorBody = (error: unknown) => {
  const e = error as {
    message?: string;
    code?: string;
    details?: Record<string, unknown>;
  } | null;
  return {
    status: httpStatus(error),
    code: e?.code,
    message: error instanceof Error ? error.message : String(error),
    details: e?.details,
  };
};

interface RunOutcome {
  at: string;
  ms: number;
  ok: boolean;
  body: unknown;
}

/**
 * One trigger: its inputs, a Run button, and the JSON the API answered with,
 * kept on the card so several triggers can be compared side by side.
 */
function TriggerCard({
  title,
  description,
  children,
  request,
  invalid,
  confirm,
  summary,
}: {
  title: string;
  description: string;
  children?: ReactNode;
  /** The request to send; null when the inputs are not complete yet. */
  request: () => CrmTriggerRequest | null;
  /** Why the request cannot be sent yet, shown instead of running. */
  invalid?: string;
  /** When set, Run asks first with this text (production runs are real). */
  confirm?: string;
  /** A readable account of a successful answer, shown above its JSON. */
  summary?: (body: unknown) => ReactNode;
}) {
  const { run, isRunning } = useRunCrmTrigger();
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);
  const [confirming, setConfirming] = useState(false);

  const onRun = async () => {
    const req = request();
    if (!req) return;
    const started = Date.now();
    try {
      const body = await run(req);
      setOutcome({
        at: new Date().toISOString(),
        ms: Date.now() - started,
        ok: true,
        body,
      });
    } catch (error) {
      setOutcome({
        at: new Date().toISOString(),
        ms: Date.now() - started,
        ok: false,
        body: errorBody(error),
      });
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        {children}
        {outcome && (
          <section
            aria-label={`${title} result`}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant={outcome.ok ? 'secondary' : 'destructive'}>
                {outcome.ok ? 'OK' : 'Failed'}
              </Badge>
              <span>
                {new Date(outcome.at).toLocaleTimeString('en-GB')} ·{' '}
                {outcome.ms} ms
              </span>
            </div>
            {outcome.ok && summary?.(outcome.body)}
            <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
              {pretty(outcome.body)}
            </pre>
          </section>
        )}
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-3">
        <span className="text-xs text-destructive">{invalid}</span>
        <Button
          type="button"
          onClick={confirm ? () => setConfirming(true) : onRun}
          disabled={isRunning || !!invalid}
          aria-label={`Run ${title}`}
        >
          <Play />
          {isRunning ? 'Running…' : 'Run'}
        </Button>
      </CardFooter>
      {confirm && (
        <AlertDialog open={confirming} onOpenChange={setConfirming}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Run {title} now?</AlertDialogTitle>
              <AlertDialogDescription>{confirm}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onRun}>Run it</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Card>
  );
}

/** What a test run sent and would have written, read off its answer. */
function TestRunSummary({ run }: { run: CrmJobRun }) {
  if (!run.test) return null;
  const { sentTo, messages, writes } = run.test;
  return (
    <div className="flex flex-col gap-3 rounded-md border p-3 text-sm">
      <section aria-label="Messages sent">
        <p className="font-medium">
          {messages.length === 0
            ? 'No messages: the job had nobody to tell'
            : `${messages.length} ${messages.length === 1 ? 'message' : 'messages'} sent to ${sentTo}`}
        </p>
        <ul className="mt-1 flex flex-col gap-1 text-xs">
          {messages.slice(0, LIST_LIMIT).map((m) => (
            <li key={`${m.at}.${m.meantFor}.${m.text}`}>
              <span className="text-muted-foreground">
                Meant for {m.meantFor}:
              </span>{' '}
              {m.text}
              {!m.delivered && (
                <Badge variant="destructive" className="ml-2">
                  Not delivered
                </Badge>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section aria-label="Twenty changes not made">
        <p className="font-medium">
          {writes.length === 0
            ? 'Nothing it would write to Twenty'
            : `${writes.length} Twenty ${writes.length === 1 ? 'change' : 'changes'} it would make (none made)`}
        </p>
        <ul className="mt-1 flex flex-col gap-1 text-xs">
          {writes.slice(0, LIST_LIMIT).map((w, i) => (
            <li key={`${w.method}.${w.id ?? `new-${i}`}`}>
              <code>{w.method}</code>{' '}
              {w.url ? (
                <a
                  href={w.url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {w.id}
                </a>
              ) : (
                w.id
              )}
            </li>
          ))}
        </ul>
        {writes.length > LIST_LIMIT && (
          <p className="mt-1 text-xs text-muted-foreground">
            And {writes.length - LIST_LIMIT} more, in the JSON below.
          </p>
        )}
      </section>
    </div>
  );
}

/**
 * One scheduled job, run as a test or for real.
 *
 * - Test (the default): a dry run that writes nothing to Twenty and marks
 *   nothing as sent; every message goes to one teammate, labelled with who it
 *   was meant for. It may run as of any time, on production too.
 * - Real: what the schedule does. It asks first, naming who will hear, and
 *   moves the clock only on a preview, where a pretend time cannot send a real
 *   reminder at the wrong moment.
 */
function JobCard({ job, preview }: { job: CrmTriggerJob; preview: boolean }) {
  const id = useId();
  const { data: session } = useSession();
  const [mode, setMode] = useState<CrmJobRunMode>('test');
  const [typed, setTyped] = useState<string | null>(null);
  const email = (typed ?? session?.user?.email ?? '').trim();
  const [asOf, setAsOf] = useState('');
  const canMoveClock = preview || mode === 'test';
  const badEmail = mode === 'test' && !TEAM_EMAIL.test(email.toLowerCase());
  const badDate = canMoveClock && asOf !== '' && toIso(asOf) === undefined;
  return (
    <TriggerCard
      title={crmTriggerJobLabels[job]}
      description={JOB_NOTES[job]}
      invalid={
        badEmail
          ? 'Enter a Borradh email (@borradh.io)'
          : badDate
            ? 'That date is not valid'
            : undefined
      }
      confirm={
        mode === 'real'
          ? `Messages go to: ${REAL_RECIPIENTS[job]}${preview ? '' : ` ${PRODUCTION_RUN}`}`
          : undefined
      }
      summary={(body) => <TestRunSummary run={body as CrmJobRun} />}
      request={() => ({
        kind: 'job',
        job,
        input:
          mode === 'test'
            ? { mode, email, now: toIso(asOf) }
            : { mode, now: preview ? toIso(asOf) : undefined },
      })}
    >
      <fieldset className="grid gap-2">
        <legend className="mb-2 text-sm font-medium">Send messages to</legend>
        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as CrmJobRunMode)}
          className="gap-2"
        >
          {crmJobRunModeValues.map((value) => (
            <div key={value} className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-${value}`} value={value} />
              <Label htmlFor={`${id}-${value}`} className="font-normal">
                {crmJobRunModeLabels[value]}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
      {mode === 'test' ? (
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-email`}>Their email</Label>
          <Input
            id={`${id}-email`}
            type="email"
            value={email}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="name@borradh.io"
            aria-invalid={badEmail}
          />
          <p className="text-xs text-muted-foreground">
            Nothing is written to Twenty or marked as sent. Every message goes
            to this person, labelled with who it was meant for, so a message
            already sent today is sent to them again.
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Goes to: {REAL_RECIPIENTS[job]}
        </p>
      )}
      {canMoveClock && (
        <div className="grid gap-1.5">
          <Label htmlFor={id}>Run as of (optional)</Label>
          <Input
            id={id}
            type="datetime-local"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </div>
      )}
    </TriggerCard>
  );
}

function GranolaImportCard({ preview }: { preview: boolean }) {
  const id = useId();
  const [noteId, setNoteId] = useState('');
  const [inline, setInline] = useState(true);
  return (
    <TriggerCard
      title="Re-import a Granola note"
      description="Fetches one call note from Granola again and writes it to the attendee’s clinic or opportunity in Twenty, as the webhook and the 5-minute poll do. Inline runs it here and shows the outcome; otherwise it is queued for the worker as a manual import."
      invalid={noteId.trim() ? undefined : 'Enter a Granola note id'}
      confirm={preview ? undefined : PRODUCTION_RUN}
      request={() => ({
        kind: 'granola-import',
        input: { granolaNoteId: noteId.trim(), inline },
      })}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={id}>Granola note id</Label>
        <Input
          id={id}
          value={noteId}
          onChange={(e) => setNoteId(e.target.value)}
          placeholder="e.g. 7f3c…"
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-inline`}
          checked={inline}
          onCheckedChange={(v) => setInline(v === true)}
        />
        <Label htmlFor={`${id}-inline`}>Run inline and show the outcome</Label>
      </div>
    </TriggerCard>
  );
}

function OnboardingSignalCard() {
  const id = useId();
  const [org, setOrg] = useState<SelectedOrg | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [signal, setSignal] = useState<OnboardingSignal>('meta_connected');
  const [on, setOn] = useState(true);
  const clinicMissing = !org && !companyId.trim();
  return (
    <TriggerCard
      title="Simulate an onboarding signal"
      description="Makes Meta connected, Stripe payments or data received read as on or off for one clinic by writing the rows the hourly onboarding sync reads (a placeholder Meta row, a Stripe Connect row with charges enabled, a marked import run). Then run “Onboarding sync and alerts” to see the checklist tick in Twenty. Preview database only; “off” removes only what this page added."
      invalid={
        clinicMissing ? 'Pick a clinic or enter a Twenty company id' : undefined
      }
      request={() => ({
        kind: 'onboarding-signal',
        input: {
          ...(org
            ? { organizationId: org.id }
            : { twentyCompanyId: companyId.trim() }),
          signal,
          on,
        },
      })}
    >
      <div className="grid gap-1.5">
        <Label>Clinic (Borradh organization)</Label>
        <OrgCombobox value={org} onChange={setOrg} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-company`}>Or a Twenty company id</Label>
        <Input
          id={`${id}-company`}
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          placeholder="Resolved through the company’s borradhOrgId"
          disabled={!!org}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-signal`}>Signal</Label>
        <Select
          value={signal}
          onValueChange={(v) => setSignal(v as OnboardingSignal)}
        >
          <SelectTrigger id={`${id}-signal`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {onboardingSignalValues.map((value) => (
              <SelectItem key={value} value={value}>
                {onboardingSignalLabels[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Switch id={`${id}-on`} checked={on} onCheckedChange={setOn} />
        <Label htmlFor={`${id}-on`}>
          {on ? 'Mark as present' : 'Mark as absent'}
        </Label>
      </div>
    </TriggerCard>
  );
}

/** A test DM. On production it goes only to the signed-in admin (the API enforces it too). */
function SlackDmCard({ preview }: { preview: boolean }) {
  const id = useId();
  const { data: session } = useSession();
  const [typed, setTyped] = useState('');
  const email = preview ? typed : (session?.user?.email ?? '');
  const [text, setText] = useState('Test DM from the CRM trigger page.');
  const valid = /\S+@\S+\.\S+/.test(email.trim()) && text.trim().length > 0;
  return (
    <TriggerCard
      title={preview ? 'Slack DM a teammate' : 'Slack DM yourself'}
      description={
        preview
          ? 'One message from the Borradh CRM Slack app to a teammate, found by their work email, through the same notifier every CRM alert uses. not_in_slack means nobody in Slack has that email.'
          : 'One message from the Borradh CRM Slack app to you, through the same notifier every CRM alert uses, to check it reaches Slack. On production it goes only to your own email; not_in_slack means your Slack account uses another one.'
      }
      invalid={valid ? undefined : 'Enter a work email and a message'}
      request={() => ({
        kind: 'slack-dm',
        input: { email: email.trim(), text: text.trim() },
      })}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-email`}>
          {preview ? 'Teammate email' : 'Your email'}
        </Label>
        <Input
          id={`${id}-email`}
          type="email"
          value={email}
          onChange={(e) => setTyped(e.target.value)}
          readOnly={!preview}
          placeholder="name@borradh.io"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-text`}>Message</Label>
        <Textarea
          id={`${id}-text`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
        />
      </div>
    </TriggerCard>
  );
}

function StatusBar({
  status,
  isLoading,
  error,
}: {
  status: CrmTriggersStatus | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading)
    return <Skeleton className="h-10 w-full" aria-label="Loading status" />;
  if (error)
    return (
      <Alert variant="destructive">
        <AlertTitle>
          {httpStatus(error) === 404
            ? 'Not available here'
            : 'Could not read the host status'}
        </AlertTitle>
        <AlertDescription>
          {httpStatus(error) === 404
            ? 'This API does not serve the CRM triggers yet (it answered 404): it is older than the page.'
            : error.message}
        </AlertDescription>
      </Alert>
    );
  if (!status) return null;
  return (
    <div className="flex flex-col gap-3">
      {!status.preview && (
        <Alert>
          <AlertTitle>Production</AlertTitle>
          <AlertDescription>
            A job runs as a test unless you pick Real: it writes nothing and
            every message goes to the email you give. A real run, and every
            other trigger here, is real and asks first. A real run as of another
            time and the simulated onboarding signals are only on previews.
          </AlertDescription>
        </Alert>
      )}
      <DepsBar status={status} />
    </div>
  );
}

function DepsBar({ status }: { status: CrmTriggersStatus }) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-sm"
      aria-label="Host dependencies"
    >
      <span className="text-muted-foreground">This host has:</span>
      {(Object.keys(DEP_LABELS) as (keyof CrmTriggersStatus['deps'])[]).map(
        (dep) => (
          <Badge key={dep} variant={status.deps[dep] ? 'default' : 'outline'}>
            {DEP_LABELS[dep]}: {status.deps[dep] ? 'yes' : 'no'}
          </Badge>
        )
      )}
      <span className="text-xs text-muted-foreground">
        A job whose dependency is missing answers <code>skipped</code>.
      </span>
    </div>
  );
}

/**
 * The CRM trigger page: every CRM automation, fired by hand. On production
 * (and until the API has said otherwise) the parts that fake data or time
 * are left out and every run asks first.
 */
export function CrmTriggersPage() {
  const { data: status, isLoading, error } = useCrmTriggersStatus();
  const preview = status?.preview === true;
  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">CRM triggers</h1>
        <p className="text-sm text-muted-foreground">
          Fire each CRM automation now instead of waiting for its schedule.
          Every run answers with the job’s own result. Jobs read and write the
          Twenty workspace this host is pointed at and DM real teammates in
          Slack; the simulated signals (previews only) write only the preview’s
          database.
        </p>
      </header>
      <StatusBar status={status} isLoading={isLoading} error={error} />
      <HandoffFlowCard />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {crmTriggerJobValues.map((job) => (
          <JobCard key={job} job={job} preview={preview} />
        ))}
        <GranolaImportCard preview={preview} />
        {preview && <OnboardingSignalCard />}
        <SlackDmCard preview={preview} />
      </div>
    </div>
  );
}
